/**
 * Shared, PURE (fs-free) cores for the DCS site-file emitters:
 * `sitemap.xml`, `robots.txt`, and `llms.txt`.
 *
 * This is the SINGLE SOURCE OF TRUTH for the three site-wide static files. Both
 * cms's own `dcsSeoPlugin` (the "factory" emit path) and — as a follow-up on a
 * separate branch — kit-vite's `dcsSitemapPlugin` import these cores so their
 * outputs are byte-identical. The file name/shape deliberately mirror the
 * proven kit-vite emitter (`packages/kit-vite/src/sitemap.ts`) so the eventual
 * DRY port is a one-line re-export swap.
 *
 * These cores reuse cms's single SEO source of truth — `resolvePageSeo` for
 * each route's canonical + robots — so the `<loc>` written into `sitemap.xml`
 * (and the link written into `llms.txt`) is byte-identical to the
 * `<link rel="canonical">` the SEO emitter bakes into each page's `<head>`. No
 * head-tag or manifest logic is forked here.
 *
 * Everything in this module is pure string-in / string-out and never throws.
 * The filesystem work lives in the plugin layer (`dcsSeoPlugin`), exactly like
 * the existing `headTags` / `pagesManifest` split — so these cores stay
 * Vue/Vite-free and unit-testable under jsdom.
 */

import { resolvePageSeo } from './headTags'
import type { PageRouteEntry } from './pagesManifest'
import type { SeoConfiguration } from '../types/seo'

// =============================================================================
// Shared helpers
// =============================================================================

const XML_HEADER = '<?xml version="1.0" encoding="UTF-8"?>'
const URLSET_NS = 'http://www.sitemaps.org/schemas/sitemap/0.9'

/**
 * AI-crawler user agents emitted as explicit allow/deny groups in robots.txt so
 * AI discovery is opt-in-friendly (and explicitly gated off in preview). A site
 * can opt the whole tier out via `robots.aiBots: false`.
 */
export const AI_BOTS = [
  'GPTBot',
  'ClaudeBot',
  'PerplexityBot',
  'Google-Extended',
  'CCBot',
  'OAI-SearchBot',
  'Applebot-Extended',
] as const

/** Trim a single trailing slash so joins never produce `//`. */
function trimTrailingSlash(url: string): string {
  return url.replace(/\/+$/, '')
}

/**
 * Sanitize a site-controlled string (title/description) before it is
 * interpolated into a single Markdown/text LINE of `llms.txt`.
 *
 * `llms.txt` is line-oriented Markdown; raw site free-text could carry newlines
 * or control chars that break the line structure (e.g. injecting a fake `##`
 * heading or a `- [x](javascript:...)` list item), or Markdown link/heading
 * sigils that hijack the line. We:
 *   - collapse ALL newlines / carriage returns / control chars to a space,
 *   - neutralize the line-leading Markdown sigils that would change block
 *     structure (`#` heading, `>` blockquote, `-`/`*`/`+` list, `[` link),
 *   - defang the `](` link sequence so `[text](javascript:...)` can't form an
 *     active link,
 * then collapse runs of whitespace. Pure: string in, single-line string out.
 */
function sanitizeLlmsText(value: string | undefined): string {
  if (!value) return ''
  return value
    // newlines, tabs, and other C0/C1 control chars -> space (kills breakout)
    .replace(/[\u0000-\u001f\u007f-\u009f]+/g, ' ')
    // defang any Markdown link target: `](` → `] (` so it can't resolve to a URL
    .replace(/\]\(/g, '] (')
    // neutralize the remaining link-open bracket so `[text]` isn't a link label
    .replace(/[[\]]/g, ' ')
    // collapse whitespace runs and trim
    .replace(/\s+/g, ' ')
    .trim()
    // neutralize a line-leading block sigil (heading/blockquote/list) the value
    // could still start with after trimming, so it can't open a new block
    .replace(/^([#>*+-]+)/, '\\$1')
}

/** XML-escape the five predefined entities for a `<loc>` value. */
function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
}

/**
 * Convert a `pages.yaml` top-level `excluded:` glob (e.g. `/dev-*`, `/_*`,
 * `/404`) into a matcher. Only `*` is treated as a wildcard; everything else is
 * literal. Honors the coron8-shape top-level `excluded:` list, which cms's
 * manifest parser ignores.
 */
function globToRegExp(glob: string): RegExp {
  const escaped = glob.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*')
  return new RegExp(`^${escaped}$`)
}

/**
 * True if `routePath` matches any glob in `excludedGlobs`. Exported so the
 * per-route HTML emitter applies the IDENTICAL exclusion semantics as the
 * sitemap/llms path (single source of truth for "excluded").
 */
export function matchesExcludedGlob(routePath: string, excludedGlobs: string[]): boolean {
  return excludedGlobs.some((g) => globToRegExp(g).test(routePath))
}

/**
 * W3C-datetime / ISO-8601 shapes the sitemaps spec accepts for `<lastmod>`:
 *   `YYYY`, `YYYY-MM`, `YYYY-MM-DD`,
 *   `YYYY-MM-DDThh:mmTZD`, `YYYY-MM-DDThh:mm:ssTZD`, `…ss.s+TZD`
 * where `TZD` is `Z` or `±hh:mm`. We accept the full-date and richer datetime
 * forms (the only ones sites actually author). Anything else is invalid.
 */
const W3C_DATETIME_RE =
  /^\d{4}(-\d{2}(-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})?)?)?)?$/

/**
 * Validate a candidate `<lastmod>` value as a W3C datetime AND a real calendar
 * instant. Returns the trimmed value when valid, else `undefined` (caller omits
 * the `<lastmod>` rather than emitting garbage into the sitemap).
 *
 * Three gates:
 *   1. the lexical shape (W3C_DATETIME_RE),
 *   2. `Date.parse` is not NaN,
 *   3. for a `YYYY-MM-DD` prefix, the parsed UTC Y/M/D round-trips back to the
 *      authored Y/M/D — this rejects shape-valid-but-impossible dates like
 *      `2026-02-30`, which JS's lenient `Date.parse` would otherwise roll over
 *      to March 2 (and thus silently corrupt the emitted lastmod).
 */
function validW3CLastmod(value: string | undefined): string | undefined {
  if (typeof value !== 'string') return undefined
  const v = value.trim()
  if (!v) return undefined
  if (!W3C_DATETIME_RE.test(v)) return undefined

  const ms = Date.parse(v)
  if (Number.isNaN(ms)) return undefined

  // Strict calendar round-trip ONLY for a date-only `YYYY-MM-DD` value (parsed
  // as UTC midnight, so the UTC Y/M/D must equal the authored Y/M/D). This
  // rejects 02-30 / 04-31 etc. We skip this for datetimes carrying a time/zone,
  // where a timezone offset can legitimately shift the UTC calendar day.
  const dateOnly = v.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (dateOnly) {
    const [, y, m, d] = dateOnly
    const dt = new Date(ms)
    if (
      dt.getUTCFullYear() !== Number(y) ||
      dt.getUTCMonth() + 1 !== Number(m) ||
      dt.getUTCDate() !== Number(d)
    ) {
      return undefined
    }
  }

  return v
}

/**
 * The SINGLE indexability predicate shared by `buildSitemapXml` and
 * `buildLlmsTxt` so the two outputs always agree on which routes appear.
 *
 * A route is NOT indexable when: its path/slug is in `exclude`, OR its
 * path/slug is in `noindex`, OR its path matches an `excludedGlobs` entry, OR
 * its resolved robots (from `seo.yaml`) matches `/noindex/i`.
 */
export function isRouteIndexable(
  route: PageRouteEntry,
  seoConfig: SeoConfiguration | undefined,
  opts: { exclude?: Set<string>; noindex?: Set<string>; excludedGlobs?: string[] } = {}
): boolean {
  const { exclude, noindex, excludedGlobs = [] } = opts

  if (exclude && (exclude.has(route.path) || (route.slug && exclude.has(route.slug)))) return false
  if (noindex && (noindex.has(route.path) || (route.slug && noindex.has(route.slug)))) return false
  if (matchesExcludedGlob(route.path, excludedGlobs)) return false

  const resolved = resolvePageSeo(route.slug, route.path, seoConfig, route.title)
  if (/noindex/i.test(resolved.robots)) return false

  return true
}

/**
 * Resolve the absolute `<loc>`/link for a route, preferring the canonical baked
 * by the shared cms resolver (single-source reuse) and falling back to
 * `siteUrl + path`. Returns `null` when neither is derivable.
 */
function resolveLoc(
  route: PageRouteEntry,
  seoConfig: SeoConfiguration | undefined,
  siteUrl: string | undefined
): string | null {
  const resolved = resolvePageSeo(route.slug, route.path, seoConfig, route.title)
  if (resolved.canonical) return resolved.canonical
  if (siteUrl) return `${trimTrailingSlash(siteUrl)}${route.path}`
  return null
}

// =============================================================================
// sitemap.xml
// =============================================================================

export interface BuildSitemapParams {
  /** Route list (from `loadPagesManifest`). */
  routes: PageRouteEntry[]
  /** Canonical production origin (preferred base when no per-page canonical). */
  siteUrl?: string
  /** cms seo config (passed straight through to `resolvePageSeo`). */
  seoConfig?: SeoConfiguration
  /** Routes to skip entirely (path or slug). */
  exclude?: string[]
  /** Routes forced to noindex / omitted (path or slug). */
  noindex?: string[]
  /** `pages.yaml` top-level `excluded:` globs (coron8 parity). */
  excludedGlobs?: string[]
  /** Optional single site-wide `<lastmod>` (ISO date) — no fabricated per-page dates. */
  lastmod?: string
}

/**
 * Build the sitemap XML string from a route list. Pure: routes + siteUrl +
 * seoConfig in, XML out.
 *
 * A route is omitted when it is not indexable (see {@link isRouteIndexable}) OR
 * no absolute `<loc>` is derivable (missing siteUrl + no canonical).
 *
 * Returns the empty string when no route yields an absolute `<loc>` (the caller
 * treats this as a no-op signal — a sitemap with no absolute base is worse than
 * none).
 */
export function buildSitemapXml(params: BuildSitemapParams): string {
  const {
    routes,
    siteUrl,
    seoConfig,
    exclude = [],
    noindex = [],
    excludedGlobs = [],
    lastmod,
  } = params

  const excludeSet = new Set(exclude)
  const noindexSet = new Set(noindex)

  // De-duplicate <loc> values: two distinct routes can resolve to the SAME
  // canonical (e.g. `/` and `/home`, or an alias path), and duplicate <loc>
  // entries are invalid/penalized in a sitemap. First-wins preserves order.
  // Validated site-wide fallback lastmod (per-page values take precedence).
  const siteLastmod = validW3CLastmod(lastmod)
  const seen = new Set<string>()
  const entries: Array<{ loc: string; lastmod?: string }> = []
  for (const route of routes) {
    if (!isRouteIndexable(route, seoConfig, { exclude: excludeSet, noindex: noindexSet, excludedGlobs })) {
      continue
    }
    const loc = resolveLoc(route, seoConfig, siteUrl)
    if (!loc) continue
    if (seen.has(loc)) continue
    seen.add(loc)
    // Per-page <lastmod> (validated) wins; else the validated site-wide value;
    // else none. Both validated, so an authored bad date is dropped, never
    // emitted (one bad date invalidates the whole sitemap). Never fabricated.
    const perPage = validW3CLastmod(route.lastmod)
    entries.push({ loc, lastmod: perPage ?? siteLastmod })
  }

  // No absolute base derivable for any route → no-op signal.
  if (entries.length === 0) return ''

  // Only emit <lastmod> when the value is a valid W3C datetime — never emit an
  // unvalidated site-authored string (a bad date invalidates the whole sitemap).
  const urls = entries
    .map(({ loc, lastmod: lm }) => {
      const lastmodLine = lm ? `\n    <lastmod>${escapeXml(lm)}</lastmod>` : ''
      return `  <url>\n    <loc>${escapeXml(loc)}</loc>${lastmodLine}\n  </url>`
    })
    .join('\n')

  return `${XML_HEADER}\n<urlset xmlns="${URLSET_NS}">\n${urls}\n</urlset>\n`
}

// =============================================================================
// robots.txt
// =============================================================================

/** robots.txt override hooks. */
export interface DcsRobotsOptions {
  /** Emit a robots.txt at all (default `true`). */
  enabled?: boolean
  /** `Disallow:` lines to emit (default `[]`). */
  disallow?: string[]
  /** `Allow:` lines to emit (default `['/']` in production). */
  allow?: string[]
  /** Raw lines appended verbatim after the generated directives. */
  extra?: string[]
  /**
   * Emit explicit AI-crawler allow/deny groups (GPTBot, ClaudeBot,
   * PerplexityBot, Google-Extended, CCBot, OAI-SearchBot, Applebot-Extended).
   * Default `true` — each `Allow: /` in
   * production, each `Disallow: /` in preview. Set `false` to drop the tier.
   */
  aiBots?: boolean
  /**
   * Overwrite an existing `dist/robots.txt` (e.g. a hand-authored
   * `public/robots.txt` Vite already copied). Default `false` — do not clobber.
   */
  force?: boolean
}

export interface BuildRobotsParams {
  /** Canonical production origin (required for the absolute `Sitemap:` line). */
  siteUrl?: string
  /** Preview / staging gate: `Disallow: /`, no `Sitemap:` line. */
  preview?: boolean
  /** robots.txt override hooks. */
  robots?: DcsRobotsOptions
  /** Whether a sitemap is being emitted (drives the `Sitemap:` line). */
  hasSitemap?: boolean
}

/**
 * Build the robots.txt string. Pure: siteUrl + options in, text out.
 *
 * - **Preview mode** (`preview: true`): emits the privacy gate `User-agent: *`
 *   + `Disallow: /`, the same `Disallow: /` for each AI-bot tier (when
 *   enabled), and OMITS the `Sitemap:` line.
 * - **Production**: `User-agent: *`, any `disallow`/`allow` lines (default
 *   `Allow: /`), explicit AI-bot allow groups (when enabled), a blank line,
 *   then an absolute `Sitemap: {siteUrl}/sitemap.xml` (trailing slash trimmed).
 *   The `Sitemap:` line is omitted when no sitemap is emitted or no `siteUrl`
 *   is known.
 * - `extra` lines are appended verbatim.
 */
export function buildRobotsTxt(params: BuildRobotsParams): string {
  const { siteUrl, preview = false, robots = {}, hasSitemap = true } = params
  const { disallow = [], allow, extra = [], aiBots = true } = robots

  const lines: string[] = ['User-agent: *']

  if (preview) {
    lines.push('Disallow: /')
    if (aiBots) {
      for (const bot of AI_BOTS) {
        lines.push('')
        lines.push(`User-agent: ${bot}`)
        lines.push('Disallow: /')
      }
    }
    if (extra.length > 0) lines.push(...extra)
    return lines.join('\n') + '\n'
  }

  // Allow directives (default `Allow: /`).
  const allowLines = allow ?? ['/']
  for (const a of allowLines) lines.push(`Allow: ${a}`)
  // Disallow directives.
  for (const d of disallow) lines.push(`Disallow: ${d}`)

  // Explicit AI-crawler groups (opt-in-friendly discovery). Each group MUST
  // replay the operator's `disallow` (and `allow`) directives so a per-bot
  // group never becomes LESS restricted than `User-agent: *` — otherwise an
  // `Allow: /`-only AI group would re-open a path the operator disallowed for
  // everyone (e.g. `/admin`). robots.txt groups do NOT inherit, so the global
  // directives are repeated verbatim inside each AI group.
  if (aiBots) {
    for (const bot of AI_BOTS) {
      lines.push('')
      lines.push(`User-agent: ${bot}`)
      for (const a of allowLines) lines.push(`Allow: ${a}`)
      for (const d of disallow) lines.push(`Disallow: ${d}`)
    }
  }

  // Absolute Sitemap line (spec requires scheme+host).
  if (hasSitemap && siteUrl) {
    lines.push('')
    lines.push(`Sitemap: ${trimTrailingSlash(siteUrl)}/sitemap.xml`)
  }

  if (extra.length > 0) {
    lines.push(...extra)
  }

  return lines.join('\n') + '\n'
}

/**
 * Decide whether an EXISTING (hand-authored) `robots.txt` in `dist/` (one Vite
 * copied from `public/`) should be KEPT rather than overwritten by the factory.
 * It is kept when it is a REAL robots file: it carries a `User-agent: *` group
 * and is not an SPA-fallback HTML page. A bare `Sitemap:`-only stub (the
 * just-posh case) or an HTML shell fails and is OVERWRITTEN with the complete
 * factory robots (User-agent groups + the AI-bot tier + Sitemap), so AI
 * discovery is guaranteed even on a site that shipped a broken stub.
 *
 * Minimal-overwrite by design: a deliberate human robots (kduff's rich file, or
 * one with custom Disallow rules) is preserved — the factory never silently
 * drops hand-authored directives. `force` still overwrites unconditionally.
 * The "factory-as-single-source, validated override" gate (D6).
 *
 * Pure: string in, boolean out.
 */
export function isHandAuthoredRobotsAcceptable(text: string): boolean {
  if (!text || !text.trim()) return false
  // An SPA-fallback HTML page (the "worse than a 404" failure) starts with markup.
  if (/^\s*</.test(text)) return false
  // Must carry the global group — a bare `Sitemap:`-only stub (just-posh) has none.
  return /^[ \t]*User-agent:[ \t]*\*[ \t]*$/im.test(text)
}

// =============================================================================
// llms.txt
// =============================================================================

export interface BuildLlmsParams {
  /** Route list (from `loadPagesManifest`). */
  routes: PageRouteEntry[]
  /** Canonical production origin (preferred base when no per-page canonical). */
  siteUrl?: string
  /** cms seo config (passed straight through to `resolvePageSeo`). */
  seoConfig?: SeoConfiguration
  /** Routes to skip entirely (path or slug). */
  exclude?: string[]
  /** Routes forced to noindex / omitted (path or slug). */
  noindex?: string[]
  /** `pages.yaml` top-level `excluded:` globs (coron8 parity). */
  excludedGlobs?: string[]
}

/**
 * Build the `llms.txt` plain-text body following the emerging llms.txt
 * convention (Markdown-ish): an H1 `# {siteName}`, a one-line `> {summary}`
 * blockquote, then a `## Pages` section listing each INDEXABLE route as a
 * Markdown link `- [{title}]({canonical}): {description}`.
 *
 * Sourced from the SAME inputs as the sitemap (so there is zero new resolution
 * path): `siteName`/`defaultDescription` from `seoConfig.global`, the URL list
 * filtered through the IDENTICAL {@link isRouteIndexable} predicate, and per-page
 * title/description/canonical from `resolvePageSeo`.
 *
 * Returns the empty string (no-op) when there is no `siteName` AND no indexable
 * route with a derivable link.
 */
export function buildLlmsTxt(params: BuildLlmsParams): string {
  const { routes, siteUrl, seoConfig, exclude = [], noindex = [], excludedGlobs = [] } = params

  const excludeSet = new Set(exclude)
  const noindexSet = new Set(noindex)

  const global = seoConfig?.global ?? {}
  // siteName/summary are site-controlled free text → sanitize before they reach
  // a Markdown line (newline/heading/link-injection guard). `siteName` keeps its
  // emptiness semantics for the no-op check below, so derive a separate display.
  const siteName = global.siteName
  const summary = sanitizeLlmsText(global.defaultDescription)

  type Entry = { title: string; canonical: string; description: string }
  const entries: Entry[] = []
  for (const route of routes) {
    if (!isRouteIndexable(route, seoConfig, { exclude: excludeSet, noindex: noindexSet, excludedGlobs })) {
      continue
    }
    const loc = resolveLoc(route, seoConfig, siteUrl)
    if (!loc) continue
    const resolved = resolvePageSeo(route.slug, route.path, seoConfig, route.title)
    entries.push({
      // title + description are site-controlled free text → sanitize per-line.
      title: sanitizeLlmsText(resolved.title),
      canonical: loc,
      description: sanitizeLlmsText(resolved.description),
    })
  }

  // No-op when there is nothing meaningful to publish.
  if (!siteName && entries.length === 0) return ''

  const out: string[] = []
  out.push(`# ${sanitizeLlmsText(siteName) || entries[0]?.title || 'Site'}`)
  if (summary) {
    out.push('')
    out.push(`> ${summary}`)
  }

  if (entries.length > 0) {
    out.push('')
    out.push('## Pages')
    out.push('')
    for (const e of entries) {
      const desc = e.description ? `: ${e.description}` : ''
      out.push(`- [${e.title}](${e.canonical})${desc}`)
    }
  }

  return out.join('\n') + '\n'
}
